import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getAuthToken,
  getCurrentShopId,
  getCurrentUserRole,
} from "./auth";
import "./PurchaseCategoriesPage.css";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

const emptyForm = {
  name: "",
  purchase_type: "expense",
  active: true,
};

function PurchaseCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingCategoryId, setEditingCategoryId] =
    useState(null);

  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] =
    useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] =
    useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const token = getAuthToken();
  const shopId = getCurrentShopId();
  const role = getCurrentUserRole();

  const buildAuthConfig = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    if (role === "super_admin") {
      config.params = {
        shop_id: shopId,
      };
    }

    return config;
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError("");

      const config = buildAuthConfig();

      config.params = {
        ...(config.params || {}),
        include_inactive: includeInactive,
      };

      if (typeFilter !== "all") {
        config.params.purchase_type = typeFilter;
      }

      const response = await axios.get(
        `${API_URL}/purchase-categories`,
        config
      );

      setCategories(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (err) {
      console.error(
        "Load purchase categories error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to load purchase categories"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive, typeFilter]);

  const filteredCategories = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    if (!searchText) {
      return categories;
    }

    return categories.filter((category) =>
      String(category.name || "")
        .toLowerCase()
        .includes(searchText)
    );
  }, [categories, search]);

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};
    const cleanName = form.name.trim();

    if (!cleanName) {
      errors.name = "Category name is required";
    } else if (cleanName.length > 100) {
      errors.name =
        "Category name cannot exceed 100 characters";
    }

    if (
      !["expense", "stock_purchase"].includes(
        form.purchase_type
      )
    ) {
      errors.purchase_type =
        "Select a valid purchase type";
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const openCreateModal = () => {
    setEditingCategoryId(null);
    setForm(emptyForm);
    setFieldErrors({});
    setError("");
    setSuccessMessage("");
    setModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditingCategoryId(category.id);

    setForm({
      name: category.name || "",
      purchase_type:
        category.purchase_type || "expense",
      active: category.active !== false,
    });

    setFieldErrors({});
    setError("");
    setSuccessMessage("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);
    setEditingCategoryId(null);
    setForm(emptyForm);
    setFieldErrors({});
  };

  const saveCategory = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        name: form.name.trim(),
        purchase_type: form.purchase_type,
        active: form.active,
      };

      if (role === "super_admin") {
        payload.shop_id = shopId;
      }

      const config = buildAuthConfig();

      if (editingCategoryId) {
        await axios.put(
          `${API_URL}/purchase-categories/${editingCategoryId}`,
          payload,
          config
        );

        setSuccessMessage(
          "Purchase category updated successfully"
        );
      } else {
        await axios.post(
          `${API_URL}/purchase-categories`,
          payload,
          config
        );

        setSuccessMessage(
          "Purchase category created successfully"
        );
      }

      closeModal();
      await loadCategories();
    } catch (err) {
      console.error(
        "Save purchase category error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to save purchase category"
      );
    } finally {
      setSaving(false);
    }
  };

  const deactivateCategory = async (category) => {
    const confirmed = window.confirm(
      `Deactivate "${category.name}"?\n\nThe category will remain in purchase history but will no longer appear in the active category list.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setError("");
      setSuccessMessage("");

      await axios.delete(
        `${API_URL}/purchase-categories/${category.id}`,
        buildAuthConfig()
      );

      setSuccessMessage(
        "Purchase category deactivated successfully"
      );

      await loadCategories();
    } catch (err) {
      console.error(
        "Deactivate purchase category error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to deactivate purchase category"
      );
    }
  };

  const reactivateCategory = async (category) => {
    try {
      setError("");
      setSuccessMessage("");

      const payload = {
        name: category.name,
        purchase_type: category.purchase_type,
        active: true,
      };

      if (role === "super_admin") {
        payload.shop_id = shopId;
      }

      await axios.put(
        `${API_URL}/purchase-categories/${category.id}`,
        payload,
        buildAuthConfig()
      );

      setSuccessMessage(
        "Purchase category reactivated successfully"
      );

      await loadCategories();
    } catch (err) {
      console.error(
        "Reactivate purchase category error:",
        err.response?.data || err.message
      );

      setError(
        err.response?.data?.error ||
          "Failed to reactivate purchase category"
      );
    }
  };

  const formatType = (purchaseType) => {
    return purchaseType === "stock_purchase"
      ? "Stock Purchase"
      : "Business Expense";
  };

  return (
    <div className="purchase-categories-page">
      <div className="purchase-categories-page-header">
        <div>
          <h1>Purchase Categories</h1>

          <p className="purchase-categories-subtitle">
            Manage categories used for stock purchases and
            business expenses.
          </p>
        </div>

        <button
          type="button"
          className="purchase-category-primary-btn"
          onClick={openCreateModal}
        >
          + New Category
        </button>
      </div>

      {error && (
        <div className="purchase-category-alert purchase-category-alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="purchase-category-alert purchase-category-alert-success">
          {successMessage}
        </div>
      )}

      <div className="purchase-category-toolbar">
        <div className="purchase-category-search-wrap">
          <span className="purchase-category-search-icon">
            ⌕
          </span>

          <input
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="Search purchase categories..."
          />
        </div>

        <div className="purchase-category-toolbar-controls">
          <select
            className="purchase-category-type-filter"
            value={typeFilter}
            onChange={(event) =>
              setTypeFilter(event.target.value)
            }
          >
            <option value="all">All types</option>

            <option value="expense">
              Business expenses
            </option>

            <option value="stock_purchase">
              Stock purchases
            </option>
          </select>

          <label className="purchase-category-toggle">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) =>
                setIncludeInactive(event.target.checked)
              }
            />

            <span>Show inactive categories</span>
          </label>
        </div>
      </div>

      <div className="purchase-category-table-card">
        {loading ? (
          <div className="purchase-category-state">
            Loading purchase categories...
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="purchase-category-empty-state">
            <div className="purchase-category-empty-icon">
              C
            </div>

            <h2>
              {search
                ? "No matching categories"
                : "No purchase categories added yet"}
            </h2>

            <p>
              {search
                ? "Try another search term."
                : "Add your first category to organise stock purchases and business expenses."}
            </p>

            {!search && (
              <button
                type="button"
                className="purchase-category-primary-btn"
                onClick={openCreateModal}
              >
                + Add First Category
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="purchase-category-result-count">
              {filteredCategories.length}{" "}
              {filteredCategories.length === 1
                ? "category"
                : "categories"}
            </div>

            <div className="purchase-category-table-scroll">
              <table className="purchase-category-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Purchase Type</th>
                    <th>Status</th>

                    <th className="purchase-category-actions-heading">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredCategories.map((category) => (
                    <tr
                      key={category.id}
                      className={
                        category.active
                          ? ""
                          : "purchase-category-inactive-row"
                      }
                    >
                      <td data-label="Category">
                        <strong>{category.name}</strong>
                      </td>

                      <td data-label="Purchase Type">
                        <span
                          className={`purchase-category-type-badge ${
                            category.purchase_type ===
                            "stock_purchase"
                              ? "stock"
                              : "expense"
                          }`}
                        >
                          {formatType(
                            category.purchase_type
                          )}
                        </span>
                      </td>

                      <td data-label="Status">
                        <span
                          className={`purchase-category-status ${
                            category.active
                              ? "purchase-category-status-active"
                              : "purchase-category-status-inactive"
                          }`}
                        >
                          {category.active
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td
  data-label="Actions"
  className="purchase-category-actions-cell"
>
  <div className="purchase-category-mobile-actions">
    <button
      type="button"
      className="purchase-category-action-btn"
      onClick={() => openEditModal(category)}
    >
      Edit
    </button>

    {category.active ? (
      <button
        type="button"
        className="purchase-category-action-btn purchase-category-action-danger"
        onClick={() => deactivateCategory(category)}
      >
        Deactivate
      </button>
    ) : (
      <button
        type="button"
        className="purchase-category-action-btn purchase-category-action-success"
        onClick={() => reactivateCategory(category)}
      >
        Reactivate
      </button>
    )}
  </div>
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <div
          className="purchase-category-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div
            className="purchase-category-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="purchase-category-modal-title"
          >
            <div className="purchase-category-modal-header">
              <h2 id="purchase-category-modal-title">
                {editingCategoryId
                  ? "Edit Purchase Category"
                  : "New Purchase Category"}
              </h2>

              <button
                type="button"
                className="purchase-category-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form
              className="purchase-category-form"
              onSubmit={saveCategory}
            >
              <div className="purchase-category-form-body">
                <section className="purchase-category-form-section">
                  <div className="purchase-category-section-heading">
                    <h3>Category details</h3>

                    <p>
                      Enter the category name and choose
                      whether it is used for stock purchases
                      or business expenses.
                    </p>
                  </div>

                  <div className="purchase-category-form-grid">
                    <div className="purchase-category-field">
                      <label htmlFor="purchase-category-name">
                        Category Name <span>*</span>
                      </label>

                      <input
                        id="purchase-category-name"
                        type="text"
                        value={form.name}
                        onChange={(event) =>
                          updateField(
                            "name",
                            event.target.value
                          )
                        }
                        className={
                          fieldErrors.name
                            ? "input-error"
                            : ""
                        }
                        placeholder="Example: Electricity"
                        maxLength={100}
                        autoFocus
                      />

                      {fieldErrors.name && (
                        <small className="purchase-category-field-error">
                          {fieldErrors.name}
                        </small>
                      )}
                    </div>

                    <div className="purchase-category-field">
                      <label htmlFor="purchase-category-type">
                        Purchase Type <span>*</span>
                      </label>

                      <select
                        id="purchase-category-type"
                        value={form.purchase_type}
                        onChange={(event) =>
                          updateField(
                            "purchase_type",
                            event.target.value
                          )
                        }
                        className={
                          fieldErrors.purchase_type
                            ? "input-error"
                            : ""
                        }
                      >
                        <option value="expense">
                          Business Expense
                        </option>

                        <option value="stock_purchase">
                          Stock Purchase
                        </option>
                      </select>

                      {fieldErrors.purchase_type && (
                        <small className="purchase-category-field-error">
                          {fieldErrors.purchase_type}
                        </small>
                      )}
                    </div>

                    {editingCategoryId && (
                      <div className="purchase-category-field">
                        <label htmlFor="purchase-category-active">
                          Status
                        </label>

                        <select
                          id="purchase-category-active"
                          value={
                            form.active
                              ? "active"
                              : "inactive"
                          }
                          onChange={(event) =>
                            updateField(
                              "active",
                              event.target.value ===
                                "active"
                            )
                          }
                        >
                          <option value="active">
                            Active
                          </option>

                          <option value="inactive">
                            Inactive
                          </option>
                        </select>
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <div className="purchase-category-modal-footer">
                <button
                  type="button"
                  className="purchase-category-secondary-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="purchase-category-primary-btn"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingCategoryId
                    ? "Update Category"
                    : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchaseCategoriesPage;